/**
 * @file askAnswerBlocks.ts
 * @brief Split an Ask answer into paragraphs and bullet lists (web + Mac desktop).
 *
 * @details
 * Since Ask Phase 2 (15-10-2026) the backend lists every matching item on its
 * own line starting with "- ". Rendered as one paragraph those lines ran
 * together, so a to-do or decision list read as a single sentence. This turns
 * the plain text into blocks the panel renders as text, never as HTML: a run
 * of "- " (or "• ") lines becomes one list, any other run of lines becomes one
 * paragraph, and a blank line ends a block. The phone needs none of this:
 * React Native <Text> keeps the line breaks (FRONTEND AskMeetingsSheet.tsx).
 *
 * `askAnswerDir` picks right-to-left when most letters are Urdu/Arabic/Hebrew
 * script, so an English answer naming one Urdu word stays left-aligned and an
 * Urdu answer naming "Asana" stays right-aligned.
 *
 * @author DES
 * @date 16-10-2026
 */

export type AskAnswerBlock =
  | { kind: "paragraph"; lines: string[] }
  | { kind: "list"; items: string[] };

const BULLET_RE = /^[-•](?:\s+(.*))?$/;

export function askAnswerBlocks(text?: string | null): AskAnswerBlock[] {
  const blocks: AskAnswerBlock[] = [];
  if (!text) return blocks;
  let current: AskAnswerBlock | null = null;
  for (const raw of text.replace(/\r\n?/g, "\n").split("\n")) {
    const line = raw.trim();
    if (!line) {
      current = null;
      continue;
    }
    const bullet = BULLET_RE.exec(line);
    if (bullet) {
      const item = (bullet[1] ?? "").trim();
      if (!item) continue;
      if (current?.kind !== "list") {
        current = { kind: "list", items: [] };
        blocks.push(current);
      }
      current.items.push(item);
    } else {
      if (current?.kind !== "paragraph") {
        current = { kind: "paragraph", lines: [] };
        blocks.push(current);
      }
      current.lines.push(line);
    }
  }
  return blocks;
}

const RTL_LETTER_RE = /[\u0590-\u05FF\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/g;
const LTR_LETTER_RE = /[A-Za-z\u00C0-\u024F]/g;

export function askAnswerDir(text?: string | null): "rtl" | "ltr" {
  if (!text) return "ltr";
  const rtl = text.match(RTL_LETTER_RE)?.length ?? 0;
  const ltr = text.match(LTR_LETTER_RE)?.length ?? 0;
  return rtl > ltr ? "rtl" : "ltr";
}
